import { NAV_ITEMS, NavItem, NavSubItem } from '@/features/system/shared/config/navigation.config';

// ======== 타입 ========

export interface PageOption {
    id: string;
    label: string;
    page: string;
    isParent: boolean;
    parentId?: string;
    children?: string[];
}

// ======== 상수 ========

export const BASE_USER_TYPE_OPTIONS = [
    { value: 'C', label: '본사' },
    { value: 'S', label: '협력사' },
    { value: 'R', label: '라이더' },
] as const;

// ======== 내부 빌더 (모듈 로드 시 1회 실행) ========

const extractPagesFromSubmenu = (submenu: NavSubItem[]): string[] => {
    const pages: string[] = [];
    submenu.forEach(sub => {
        if (sub.page) pages.push(sub.page);
        sub.children?.forEach(child => {
            if (child.page) pages.push(child.page);
        });
    });
    return pages;
};

const _buildPageOptions = (): PageOption[] => {
    const options: PageOption[] = [];

    NAV_ITEMS.forEach((navItem: NavItem) => {
        const childPages = navItem.submenu ? extractPagesFromSubmenu(navItem.submenu) : [];
        options.push({
            id: navItem.id,
            label: navItem.label,
            page: navItem.id,
            isParent: true,
            children: childPages,
        });

        navItem.submenu?.forEach((subItem: NavSubItem) => {
            if (subItem.children) {
                const childPages = subItem.children
                    .map(child => child.page)
                    .filter((page): page is string => !!page);

                options.push({
                    id: subItem.id,
                    label: subItem.label,
                    page: subItem.id,
                    isParent: true,
                    parentId: navItem.id,
                    children: childPages,
                });

                subItem.children.forEach(child => {
                    if (child.page) {
                        options.push({
                            id: child.id,
                            label: child.label,
                            page: child.page,
                            isParent: false,
                            parentId: subItem.id,
                        });
                    }
                });
            } else if (subItem.page) {
                options.push({
                    id: subItem.id,
                    label: subItem.label,
                    page: subItem.page,
                    isParent: false,
                    parentId: navItem.id,
                });
            }
        });
    });

    return options;
};

const _buildGroupedPageOptions = (
    pageOptions: PageOption[]
): { parent: PageOption; children: PageOption[] }[] => {
    const grouped: { parent: PageOption; children: PageOption[] }[] = [];
    pageOptions.forEach(option => {
        if (option.isParent) {
            const children = pageOptions.filter(opt => !opt.isParent && opt.parentId === option.id);
            grouped.push({ parent: option, children });
        }
    });
    return grouped;
};

const _buildPageCodeToLabelMap = (): Record<string, string> => {
    const map: Record<string, string> = {};
    NAV_ITEMS.forEach((navItem: NavItem) => {
        map[navItem.id] = navItem.label;
        navItem.submenu?.forEach((subItem: NavSubItem) => {
            map[subItem.id] = subItem.label;
            if (subItem.page) map[subItem.page] = subItem.label;
            subItem.children?.forEach(child => {
                map[child.id] = child.label;
                if (child.page) map[child.page] = child.label;
            });
        });
    });
    return map;
};

// 모듈 로드 시 1회만 계산
const PAGE_OPTIONS = _buildPageOptions();
const GROUPED_PAGE_OPTIONS = _buildGroupedPageOptions(PAGE_OPTIONS);
const PAGE_CODE_TO_LABEL_MAP = _buildPageCodeToLabelMap();

// ======== 공개 API ========

export const buildPageOptions = (): PageOption[] => PAGE_OPTIONS;

export const getGroupedPageOptions = (): { parent: PageOption; children: PageOption[] }[] =>
    GROUPED_PAGE_OPTIONS;

export const buildPageCodeToLabelMap = (): Record<string, string> => PAGE_CODE_TO_LABEL_MAP;

/**
 * 선택된 페이지에서 실제 접근 가능한 페이지 목록 계산
 */
export const calculateAccessiblePages = (
    selectedPages: string[],
    pageOptions: PageOption[]
): string[] => {
    const accessiblePages = new Set<string>();

    selectedPages.forEach(selectedId => {
        const option = pageOptions.find(opt => opt.id === selectedId);
        if (!option) return;

        if (option.isParent && option.children) {
            option.children.forEach(childPage => accessiblePages.add(childPage));
        } else {
            accessiblePages.add(option.page);
        }
    });

    return Array.from(accessiblePages);
};

/**
 * 폼 유효성 검사
 */
export const validateForm = (formData: {
    prmCode: string;
    prmName: string;
    baseUserType: string;
    accessPages: string[];
}): boolean => {
    return !!(
        formData.prmCode.trim() &&
        formData.prmName.trim() &&
        formData.baseUserType.trim() &&
        formData.accessPages.length > 0
    );
};

/**
 * 쉼표로 구분된 페이지 코드 문자열을 한글 라벨로 변환
 */
export const convertPageCodesToLabels = (pagesStr: string): string => {
    if (!pagesStr) return '';

    const codes = pagesStr.split(',').map(code => code.trim());
    const labels = codes
        .map(code => PAGE_CODE_TO_LABEL_MAP[code] || code)
        .filter((label, index, self) => self.indexOf(label) === index);

    return labels.join(', ');
};
